<section class="entlastung-detail-page">
  @if (isLoading()) {
    <header class="lovable-detail-header">
      <div class="lovable-header-copy">
        <div class="skeleton skeleton-back"></div>
        <div class="skeleton skeleton-title"></div>
        <div class="skeleton skeleton-subline"></div>
      </div>
      <div class="skeleton skeleton-date"></div>
    </header>
  } @else {
    @if (detail(); as currentDetail) {
      <header class="lovable-detail-header">
        <div class="lovable-header-copy">
          <button type="button" class="back-button" (click)="goBack()">
            <mat-icon fontSet="material-icons-outlined">arrow_back</mat-icon>
            <span>Zurück zur Übersicht</span>
          </button>

          <h1>{{ currentDetail.filialName }}</h1>
          <p>Artikel suchen, Grund, Einheit und Menge erfassen</p>
        </div>

        <div class="date-box lovable-date-box">
          <mat-icon fontSet="material-icons-outlined">calendar_today</mat-icon>
          <span>{{ datumLabel() }}</span>
        </div>
      </header>

      <main class="lovable-detail-content">
        <div class="add-row">
          <button type="button" class="add-article-button" (click)="openAddDialog()">
            <mat-icon fontSet="material-icons-outlined">add</mat-icon>
            <span>Artikel hinzufügen</span>
          </button>
        </div>

        @if (rows().length === 0) {
          <section class="lovable-empty-state" aria-label="Keine Artikel erfasst">
            <mat-icon fontSet="material-icons-outlined">inventory_2</mat-icon>
            <p>Noch keine Artikel erfasst</p>
            <span>{{ hasUnsavedChanges() ? 'Bitte Änderungen speichern oder einen Artikel hinzufügen' : 'Tippe auf „Artikel hinzufügen“, um zu starten' }}</span>
          </section>
        } @else {
          <div class="position-count">{{ rows().length }} Positionen erfasst</div>

          <section class="position-table" aria-label="Entlastungspositionen">
            <div class="table-scroll">
              <div class="table-header" aria-hidden="true">
                <span>Artikel</span>
                <span class="numeric">Menge</span>
                <span>Einheit</span>
                <span>Grund</span>
                <span>Status</span>
                <span class="numeric">Aktion</span>
              </div>

              <div class="table-body">
                @for (row of rows(); track row.rowId) {
                  <div
                    class="table-row table-row-clickable"
                    [class.table-row-open]="row.menge <= 0"
                    [class.table-row-active]="activeRowId() === row.rowId"
                    role="button"
                    tabindex="0"
                    (click)="openNumpad(row.rowId)"
                    (keydown.enter)="openNumpad(row.rowId)"
                  >
                    <span class="artikel-cell">
                      <strong>{{ row.artikelName }}</strong>
                      <small>{{ row.artikelNummer }}</small>
                    </span>

                    <span class="numeric menge-cell">
                      <button
                        type="button"
                        class="menge-button"
                        [class.menge-button-active]="activeRowId() === row.rowId"
                        (click)="$event.stopPropagation(); openNumpad(row.rowId)"
                      >
                        {{ formatMenge(row.menge) }}
                      </button>
                    </span>

                    <span class="select-cell" (click)="$event.stopPropagation()">
                      <select
                        #unitSelect
                        class="table-select"
                        aria-label="Einheit wählen"
                        (change)="changeUnit(row.rowId, unitSelect.value)"
                      >
                        @for (
                          unit of getAllowedUnits(row.artikelId, row.einheitId, row.einheit);
                          track unit.id
                        ) {
                          <option [value]="unit.id" [selected]="unit.id === row.einheitId">
                            {{ unit.bezeichnung }}
                          </option>
                        }
                      </select>
                    </span>

                    <span class="select-cell" (click)="$event.stopPropagation()">
                      <select
                        #reasonSelect
                        class="table-select reason-select"
                        aria-label="Entlastungsgrund wählen"
                        (change)="changeReason(row.rowId, reasonSelect.value)"
                      >
                        @for (
                          reason of getAllowedReasons(row.artikelId, row.grundId);
                          track reason.id
                        ) {
                          <option [value]="reason.id" [selected]="reason.id === row.grundId">
                            {{ reason.name }}
                          </option>
                        }
                      </select>
                    </span>

                    <span
                      class="position-status"
                      [class.position-status-erfasst]="row.menge > 0"
                      [class.position-status-offen]="row.menge <= 0"
                    >
                      {{ row.menge > 0 ? 'Erfasst' : 'Offen' }}
                    </span>

                    <span class="numeric action-cell" (click)="$event.stopPropagation()">
                      <button
                        type="button"
                        class="remove-button"
                        aria-label="Position entfernen"
                        title="Position entfernen"
                        (click)="removeRow(row.rowId)"
                      >
                        <mat-icon fontSet="material-icons-outlined">close</mat-icon>
                      </button>
                    </span>
                  </div>
                }
              </div>
            </div>
          </section>

          <section class="mobile-position-list" aria-label="Entlastungspositionen mobil">
            @for (row of rows(); track row.rowId) {
              <article
                class="mobile-position-card"
                [class.mobile-position-card-active]="activeRowId() === row.rowId"
              >
                <div class="mobile-position-head" (click)="openNumpad(row.rowId)">
                  <div class="mobile-article-copy">
                    <strong>{{ row.artikelName }}</strong>
                    <small>{{ row.artikelNummer }}</small>
                  </div>
                </div>

                <div class="mobile-card-fields">
                  <div class="mobile-card-field">
                    <span class="mobile-field-label">Status</span>
                    <span
                      class="position-status"
                      [class.position-status-erfasst]="row.menge > 0"
                      [class.position-status-offen]="row.menge <= 0"
                    >
                      {{ row.menge > 0 ? 'Erfasst' : 'Offen' }}
                    </span>
                  </div>

                  <div class="mobile-card-field">
                    <span class="mobile-field-label">Menge</span>
                    <button
                      type="button"
                      class="mobile-quantity-value"
                      (click)="openNumpad(row.rowId)"
                    >
                      <strong>{{ formatMenge(row.menge) }}</strong>
                      <span>{{ row.einheit }}</span>
                    </button>
                  </div>

                  <label class="mobile-card-field">
                    <span class="mobile-field-label">Einheit</span>
                    <select
                      #mobileUnitSelect
                      class="table-select mobile-field-control"
                      (change)="changeUnit(row.rowId, mobileUnitSelect.value)"
                    >
                      @for (
                        unit of getAllowedUnits(row.artikelId, row.einheitId, row.einheit);
                        track unit.id
                      ) {
                        <option [value]="unit.id" [selected]="unit.id === row.einheitId">
                          {{ unit.bezeichnung }}
                        </option>
                      }
                    </select>
                  </label>

                  <label class="mobile-card-field">
                    <span class="mobile-field-label">Grund</span>
                    <select
                      #mobileReasonSelect
                      class="table-select mobile-field-control"
                      (change)="changeReason(row.rowId, mobileReasonSelect.value)"
                    >
                      @for (
                        reason of getAllowedReasons(row.artikelId, row.grundId);
                        track reason.id
                      ) {
                        <option [value]="reason.id" [selected]="reason.id === row.grundId">
                          {{ reason.name }}
                        </option>
                      }
                    </select>
                  </label>

                  <div class="mobile-card-field mobile-card-action-field">
                    <span class="mobile-field-label">Aktion</span>
                    <button
                      type="button"
                      class="mobile-remove-action"
                      aria-label="Position entfernen"
                      (click)="removeRow(row.rowId)"
                    >
                      <mat-icon fontSet="material-icons-outlined">close</mat-icon>
                      <span>Entfernen</span>
                    </button>
                  </div>
                </div>
              </article>
            }
          </section>


        }
          @if (rows().length > 0 || hasUnsavedChanges() || isSaving()) {
          <div class="lovable-save-bar">
            <button
              type="button"
              class="save-button"
              [disabled]="isSaving() || !hasUnsavedChanges()"
              (click)="save()"
            >
              @if (isSaving()) {
                <mat-icon class="spin" fontSet="material-icons-outlined">progress_activity</mat-icon>
                <span>Speichern…</span>
              } @else if (!hasUnsavedChanges()) {
                <mat-icon fontSet="material-icons-outlined">check</mat-icon>
                <span>Gespeichert</span>
              } @else {
                <mat-icon fontSet="material-icons-outlined">save</mat-icon>
                <span>Änderungen speichern</span>
              }
            </button>
          </div>
          }
      </main>
    }
  }
</section>

@if (activeRow(); as active) {
  <div class="numpad-panel" role="dialog" aria-modal="true" aria-label="Menge bearbeiten">
    <div class="numpad-inner">
      <div class="numpad-head">
        <div class="numpad-title">
          <span class="numpad-title-label">Menge:</span>
          <span class="numpad-title-article">{{ active.artikelName }}</span>
          <span class="numpad-title-number">· {{ active.artikelNummer }}</span>
        </div>

        <button type="button" class="numpad-close" aria-label="Editor schließen" (click)="closeNumpad()">
          <mat-icon fontSet="material-icons-outlined">close</mat-icon>
        </button>
      </div>

      <div class="numpad-reasons" aria-label="Entlastungsgrund">
        @for (reason of activeAllowedReasons(); track reason.id) {
          <button
            type="button"
            class="numpad-chip"
            [class.numpad-chip-active]="isActiveReason(reason.id)"
            (click)="selectActiveReason(reason.id)"
          >
            {{ reason.name }}
          </button>
        }
      </div>

      <div class="numpad-units" aria-label="Einheit">
        @for (unit of activeAllowedUnits(); track unit.id) {
          <button
            type="button"
            class="numpad-chip"
            [class.numpad-chip-active]="isActiveUnit(unit.id)"
            (click)="selectActiveUnit(unit.id)"
          >
            {{ unit.bezeichnung }}
          </button>
        }
      </div>

      <div class="numpad-display">
        <input
          #numpadInput
          type="text"
          inputmode="numeric"
          pattern="[0-9]*"
          class="numpad-input"
          aria-label="Menge"
          [value]="inputValue()"
          [attr.placeholder]="active.menge"
          (input)="setNumpadInput(numpadInput.value)"
          (keydown.enter)="$event.preventDefault(); confirmQuantity()"
        />
        <span class="numpad-unit">{{ active.einheit }}</span>
      </div>

      <div class="numpad-keys">
        @for (key of numpadKeys; track $index) {
          @if (key === null) {
            <span class="numpad-key-spacer"></span>
          } @else {
            <button
              type="button"
              class="numpad-key"
              [class.numpad-key-backspace]="key === '⌫'"
              (click)="pressNumpadKey(key)"
            >
              {{ key }}
            </button>
          }
        }
      </div>

      <div class="numpad-steps">
        <button type="button" class="numpad-step" (click)="stepMenge(-1)">
          <mat-icon fontSet="material-icons-outlined">remove</mat-icon>
          <span>1</span>
        </button>
        <button type="button" class="numpad-step" (click)="stepMenge(1)">
          <mat-icon fontSet="material-icons-outlined">add</mat-icon>
          <span>1</span>
        </button>
      </div>

      <div class="numpad-actions">
        <button type="button" class="numpad-secondary" (click)="openAddDialogFromNumpad()">
          <mat-icon fontSet="material-icons-outlined">add</mat-icon>
          <span>Weiterer Artikel</span>
        </button>
        <button type="button" class="numpad-confirm" (click)="confirmQuantity()">
          Fertig
        </button>
      </div>
    </div>
  </div>
}

@if (isAddOpen()) {
  <div class="dialog-overlay" (click)="closeAddDialog()">
    <div
      class="add-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-dialog-title"
      (click)="$event.stopPropagation()"
    >
      <h2 id="add-dialog-title">
        {{ addDialogStep() === 'ARTIKEL' ? 'Artikel suchen' : 'Grund wählen' }}
      </h2>

      <button type="button" class="dialog-close" aria-label="Dialog schließen" (click)="closeAddDialog()">
        <mat-icon fontSet="material-icons-outlined">close</mat-icon>
      </button>

      @if (addDialogStep() === 'ARTIKEL') {
        <label class="add-search">
          <mat-icon fontSet="material-icons-outlined">search</mat-icon>
          <input
            #addSearchInput
            type="text"
            placeholder="Artikelname, Artikelnummer oder Warengruppe…"
            [value]="addQuery()"
            (input)="setAddQuery(addSearchInput.value)"
          />
        </label>

        <div class="add-results">
          @if (katalogTreffer().length === 0) {
            <p class="add-empty">Kein Artikel gefunden.</p>
          } @else {
            @for (article of katalogTreffer(); track article.id) {
              <button type="button" class="add-result" (click)="chooseArticle(article)">
                <span class="add-result-copy">
                  <strong>{{ article.artikelName }}</strong>
                  <small>{{ article.artikelNummer }}</small>
                </span>
                <span class="add-result-category">{{ article.warengruppe }}</span>
              </button>
            }
          }
        </div>
      } @else {
        @if (pendingArticle(); as selectedArticle) {
        <div class="selected-article">
          <strong>{{ selectedArticle.artikelName }}</strong>
          <small>{{ selectedArticle.artikelNummer }} · {{ selectedArticle.warengruppe }}</small>
        </div>

        <div class="reason-grid">
          @for (reason of pendingReasons(); track reason.id) {
            <button type="button" class="reason-option" (click)="addWithReason(reason.id)">
              {{ reason.name }}
            </button>
          }
        </div>
        }
      }

      <div class="dialog-actions">
        @if (addDialogStep() === 'GRUND') {
          <button type="button" class="dialog-secondary" (click)="backToArticleSearch()">
            Zurück
          </button>
        }
        <button type="button" class="dialog-secondary" (click)="closeAddDialog()">
          Abbrechen
        </button>
      </div>
    </div>
  </div>
}
